import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { announcementsAPI, sportsAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { ArrowLeft, Megaphone } from 'lucide-react';
import { ANNOUNCEMENT_TYPES } from '@/lib/utils';

export const CreateAnnouncementPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [sports, setSports] = useState([]);
  
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    type: 'looking_for_players',
    sport_id: '',
    city: '',
    date_needed: ''
  });

  React.useEffect(() => {
    const fetchSports = async () => {
      try {
        const res = await sportsAPI.getAll();
        setSports(res.data);
      } catch (error) {
        console.error('Error fetching sports:', error);
      }
    };
    fetchSports();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title || !formData.content) {
      toast.error('Compila titolo e contenuto');
      return;
    }

    setLoading(true);
    try {
      await announcementsAPI.create({
        ...formData,
        sport_id: formData.sport_id || undefined,
        date_needed: formData.date_needed || undefined
      });
      toast.success('Annuncio pubblicato!');
      navigate('/search?tab=announcements');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella pubblicazione');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-4 md:p-6 lg:p-8 max-w-2xl mx-auto" data-testid="create-announcement-page">
      <Button 
        variant="ghost" 
        className="mb-6"
        onClick={() => navigate(-1)}
      >
        <ArrowLeft className="h-4 w-4 mr-2" />
        Indietro
      </Button>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Megaphone className="h-6 w-6 text-primary" />
            Crea Annuncio
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="type">Tipo Annuncio</Label>
              <Select
                value={formData.type}
                onValueChange={(value) => setFormData({ ...formData, type: value })}
              >
                <SelectTrigger data-testid="announcement-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ANNOUNCEMENT_TYPES.map(type => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="title">Titolo *</Label>
              <Input
                id="title"
                placeholder="Es: Cerco portiere per partita giovedì"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                data-testid="announcement-title-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="content">Descrizione *</Label>
              <Textarea
                id="content"
                placeholder="Descrivi cosa cerchi..."
                value={formData.content}
                onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                rows={4}
                data-testid="announcement-content-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="sport">Sport</Label>
                <Select
                  value={formData.sport_id}
                  onValueChange={(value) => setFormData({ ...formData, sport_id: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleziona" />
                  </SelectTrigger>
                  <SelectContent>
                    {sports.map(sport => (
                      <SelectItem key={sport.sport_id} value={sport.sport_id}>
                        {sport.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="city">Città</Label>
                <Input
                  id="city"
                  placeholder="Es: Milano"
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="date_needed">Data (se applicabile)</Label>
              <Input
                id="date_needed"
                type="date"
                value={formData.date_needed}
                onChange={(e) => setFormData({ ...formData, date_needed: e.target.value })}
              />
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                className="flex-1"
                onClick={() => navigate(-1)}
              >
                Annulla
              </Button>
              <Button 
                type="submit" 
                className="flex-1"
                disabled={loading}
                data-testid="submit-announcement-btn"
              >
                {loading ? 'Pubblicazione...' : 'Pubblica'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default CreateAnnouncementPage;
